"use client";

import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Sparkles, Shield, Scroll, Sword, Heart, Copy, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { LoreProfile } from "@/lib/loreGenerator";
import { toast } from "sonner";

interface LoreStoryModalProps {
  profile: LoreProfile | null;
  isOpen: boolean;
  onClose: () => void;
  onFavorite?: (name: string, meaning: string) => void;
  isFavorite?: boolean;
}

export default function LoreStoryModal({
  profile,
  isOpen,
  onClose,
  onFavorite,
  isFavorite,
}: LoreStoryModalProps) {
  const [copied, setCopied] = React.useState(false);

  if (!isOpen || !profile) return null;

  const handleCopyProfile = async () => {
    const text = `
=== ${profile.name} ===
• Meaning: ${profile.meaning}
• Archetype: ${profile.archetype}
• Alignment: ${profile.alignment}
• Recommended D&D Class: ${profile.dndClass}
• Signature Relic: ${profile.signatureRelic}
• Personality: ${profile.personalityTrait}
• Story / Quest Hook: ${profile.questHook}
    `.trim();

    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Copied full lore profile to clipboard! 📜");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Failed to copy profile");
    }
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-background/80 backdrop-blur-md"
        />

        {/* Modal Window */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="relative w-full max-w-2xl rounded-3xl border border-primary/30 bg-card p-6 sm:p-8 shadow-2xl overflow-hidden z-10"
        >
          {/* Subtle Ambient Glow */}
          <div className="absolute -top-24 -right-24 w-64 h-64 bg-primary/10 rounded-full blur-3xl pointer-events-none" />

          {/* Header */}
          <div className="flex items-start justify-between gap-4 pb-5 border-b border-border/60">
            <div>
              <div className="flex items-center gap-2 mb-1.5">
                <Badge variant="fantasy" className="text-xs">
                  {profile.archetype}
                </Badge>
                <Badge variant="outline" className="text-xs border-primary/30 text-primary">
                  {profile.alignment}
                </Badge>
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold font-fantasy text-foreground tracking-tight">
                {profile.name}
              </h2>
              <p className="text-xs sm:text-sm text-muted-foreground mt-1 leading-relaxed">
                {profile.meaning}
              </p>
            </div>

            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Profile Details Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-6">
            {/* Character Class / Archetype */}
            <div className="p-4 rounded-2xl border border-border/60 bg-muted/40 space-y-1.5">
              <div className="flex items-center gap-2 text-primary font-semibold text-xs font-fantasy">
                <Shield className="w-4 h-4" />
                <span>Character Archetype &amp; Role</span>
              </div>
              <p className="text-sm font-medium text-foreground">{profile.dndClass}</p>
            </div>

            {/* Signature Relic */}
            <div className="p-4 rounded-2xl border border-border/60 bg-muted/40 space-y-1.5">
              <div className="flex items-center gap-2 text-primary font-semibold text-xs font-fantasy">
                <Sword className="w-4 h-4" />
                <span>Signature Relic / Weapon</span>
              </div>
              <p className="text-sm font-medium text-foreground">{profile.signatureRelic}</p>
            </div>

            {/* Personality Trait */}
            <div className="sm:col-span-2 p-4 rounded-2xl border border-border/60 bg-muted/40 space-y-1.5">
              <div className="flex items-center gap-2 text-primary font-semibold text-xs font-fantasy">
                <Sparkles className="w-4 h-4" />
                <span>Distinctive Roleplay Trait</span>
              </div>
              <p className="text-xs sm:text-sm text-muted-foreground italic">
                &ldquo;{profile.personalityTrait}&rdquo;
              </p>
            </div>

            {/* Quest Hook / Backstory */}
            <div className="sm:col-span-2 p-5 rounded-2xl border border-primary/20 bg-primary/5 space-y-2">
              <div className="flex items-center gap-2 text-primary font-bold text-xs uppercase tracking-wider">
                <Scroll className="w-4 h-4" />
                <span>Story Quest Hook &amp; Backstory</span>
              </div>
              <p className="text-sm text-foreground/90 leading-relaxed font-sans">
                {profile.questHook}
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap items-center justify-between gap-3 pt-4 border-t border-border/60">
            <div className="flex items-center gap-2">
              {onFavorite && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onFavorite(profile.name, profile.meaning)}
                  className={`rounded-xl border-border/80 ${isFavorite ? "text-red-500 border-red-500/30" : ""}`}
                >
                  <Heart className={`w-4 h-4 mr-1.5 ${isFavorite ? "fill-red-500" : ""}`} />
                  {isFavorite ? "Favorited" : "Save Favorite"}
                </Button>
              )}
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleCopyProfile}
                className="rounded-xl border-border/80"
              >
                {copied ? <Check className="w-4 h-4 mr-1.5 text-green-500" /> : <Copy className="w-4 h-4 mr-1.5" />}
                {copied ? "Copied Profile!" : "Copy Full Profile"}
              </Button>
              <Button
                variant="fantasy"
                size="sm"
                onClick={onClose}
                className="rounded-xl"
              >
                Done
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
